import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverCoordinator} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {isEnhancedCard} from '../../../helpers/models/cardHelpers';
import {CardModel} from '../../../models/api/cardModel';
import {CreateNewGroupPopoverContainer} from '../common/createNewGroupPopoverContainer';
import {useContactDetailsContext} from '../contactDetailsContext';
import {buildContactDetailsCreateCardGroupEvent} from '../contactDetailsTrackingHelpers';
import {ContactHeaderAddToGroupDropdownContainer} from './contactHeaderAddToGroupDropdownContainer';
import {ContactHeaderPill} from './contactHeaderPill';

/*
 * Props.
 */

interface ContactHeaderAddToGroupPopoverProps {
  card: CardModel;
  shouldShowAddText: boolean;
}

/*
 * Styles.
 */

const StyledContactHeaderPill = styled(ContactHeaderPill)`
  display: flex;
  flex-flow: row;
  margin-top: 4px;
  margin-right: 4px;
`;

const StyledTransContainerDiv = styled.div`
  margin-left: 4px;
`;

/*
 * Component.
 */

export const ContactHeaderAddToGroupPopover: FC<ContactHeaderAddToGroupPopoverProps> = (props) => {
  const theme = useUserTheme();
  const {card, shouldShowAddText} = props;
  const [isShowingCreatePopover, setIsShowingCreatePopover] = useState(false);
  const {onTrackEvent} = useContactDetailsContext();

  if (!isEnhancedCard(card)) {
    return null;
  }

  const onNewGroupClick = () => {
    setIsShowingCreatePopover(true);
  };
  const onCreateClose = () => {
    setIsShowingCreatePopover(false);
  };

  return (
    <PopoverCoordinator
      interactionId="contact-details-add-to-group-btn"
      renderButton={(isModalOpen: boolean) => (
        <StyledContactHeaderPill
          key="add-group"
          textColor={theme.greys.shade70}
          backgroundColor={isModalOpen ? theme.greys.shade30 : theme.greys.shade20}
          hoverBackgroundColor={theme.greys.shade30}
          isIconOnly={!shouldShowAddText}
        >
          <Icon name="newTiny" color={theme.greys.shade70} />
          {shouldShowAddText && (
            <StyledTransContainerDiv>
              <Trans id="contacts-header-add-to-group">Add to list</Trans>
            </StyledTransContainerDiv>
          )}
        </StyledContactHeaderPill>
      )}
      renderModal={(popoverProps) =>
        isShowingCreatePopover ? (
          <CreateNewGroupPopoverContainer
            popoverProps={popoverProps}
            card={card}
            onCreateClose={(wasCreated, cardGroup) => {
              onCreateClose();
              // If the create was successful, close the entire popover
              if (wasCreated && cardGroup && onTrackEvent) {
                onTrackEvent(buildContactDetailsCreateCardGroupEvent(card, cardGroup));
              }

              popoverProps.onRequestClose();
            }}
          />
        ) : (
          <ContactHeaderAddToGroupDropdownContainer
            popoverProps={popoverProps}
            card={card}
            onNewGroupClick={onNewGroupClick}
          />
        )
      }
    />
  );
};
